import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate,useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getAdminPortfolioById, getSnapshotDetail, previewResetSnapshot, commitImportPreview, previewResetCsv, getPublicPortfolioBySlug } from '../../composables/fetchAPI';
import { useMarketPrices } from '../../context/MarketPriceContext';
import { useExchangeRates } from '../../context/ExchangeRateContext';
import ToggleCurrency from '../../components/ToggleCurrency';
import PortfolioSingleChartBox from '../../components/PortfolioSingleChartBox';
import PortfolioStocksBox from '../../components/PortfolioV2/PortfolioStocksBox';
import { usePortfolio } from '../../context/PortfolioContext'
import { useUser } from '../../context/UserContext'
// 📂 Modal สำหรับ Import CSV พร้อม Safety Check ป้องกันพังเวลา data เป็น null
const ImportCsvModal = ({ isOpen, onClose, portfolioId, onRefresh }: any) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any | null>(null);
  if (!isOpen) return null;

  const handlePreviewCsv = async () => {
    if (!file) {
      toast.error('กรุณาเลือกไฟล์ CSV ก่อน');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('mode', 'RESET_SNAPSHOT');
      formData.append('cashBalance', '0');
      formData.append('asofDate', new Date().toISOString());
      formData.append('timezone', 'Asia/Bangkok');
      formData.append('idempotencyKey', `csv-preview-${Date.now()}`);
      formData.append('file', file);

      const previewRes = await previewResetCsv(portfolioId, formData);
      setPreviewData(previewRes?.data || previewRes);
      toast.success('สร้างตัวอย่างพรีวิว CSV สำเร็จ');
    } catch (error: any) {
      console.error(error);
      toast.error(error?.th_message || error?.message || 'ไม่สามารถพรีวิว CSV ได้');
    } finally {
      setLoading(false);
    }
  };

  const handleCommitCsv = async () => {
    const pToken = previewData?.previewToken;
    if (!pToken) {
      toast.error('ไม่พบ Token สำหรับยืนยัน');
      return;
    }

    try {
      setLoading(true);
      await commitImportPreview(portfolioId, {
        previewToken: pToken,
        confirmationCode: previewData?.confirmation?.code || "RESET_SNAPSHOT_CONFIRM"
      } as any);

      toast.success('Import และบันทึก CSV สำเร็จ!');
      onRefresh();
      onClose();
      setFile(null);
      setPreviewData(null);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.th_message || error?.message || 'ไม่สามารถ Commit CSV ได้');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-[#18181b] border border-yellow-500/30 w-full max-w-2xl rounded-2xl p-6 text-white shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-3">
          <h2 className="text-lg font-bold text-yellow-400">📂 Import CSV (Preview & Changes)</h2>
          <button onClick={() => { onClose(); setPreviewData(null); setFile(null); }} className="text-gray-400 hover:text-white font-bold">✕</button>
        </div>

        {!previewData ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-400 mb-2 block">
                เลือกไฟล์ CSV (ตรวจสอบ Header ให้ถูกต้อง: <code className="text-yellow-400">ticker,qty,average_cost,remark</code> ระวังอย่าให้คอลัมน์ติดกัน)
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="w-full bg-black/40 border border-gray-700 rounded p-2 text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-yellow-400 file:text-black hover:file:bg-yellow-500"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-800">
              <button onClick={() => { onClose(); setPreviewData(null); setFile(null); }} className="bg-gray-700 hover:bg-gray-600 text-white font-bold px-4 py-2 rounded text-sm transition-colors">
                ยกเลิก
              </button>
              <button onClick={handlePreviewCsv} disabled={loading || !file} className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-5 py-2 rounded text-sm transition-colors disabled:opacity-50">
                {loading ? 'กำลังพรีวิว...' : '🔍 ตรวจสอบพรีวิว (Preview)'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-y-auto pr-1">
            <div className="bg-black/40 p-3 rounded-xl border border-gray-800 flex justify-between text-xs">
              <span>รวมทั้งหมด: <b>{previewData?.summary?.rowCount}</b></span>
              <span className="text-green-400">อัปเดต: <b>{previewData?.summary?.positionUpdatedCount || 0}</b></span>
              <span className="text-yellow-400">เพิ่มใหม่: <b>{previewData?.summary?.positionAddedCount || 0}</b></span>
            </div>

            <div className="border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-900 text-gray-400 border-b border-gray-800">
                    <th className="p-2.5">Ticker</th>
                    <th className="p-2.5">สถานะ</th>
                    <th className="p-2.5">ก่อนเปลี่ยน (Before)</th>
                    <th className="p-2.5">หลังเปลี่ยน (After)</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData?.changes?.map((item: any, idx: number) => {
                    const isUpdated = item.changeType === 'UPDATED';
                    const isAdded = item.changeType === 'ADDED';
                    return (
                      <tr
                        key={idx}
                        className={`border-b border-gray-800/60 ${isUpdated ? 'bg-yellow-500/10 border-l-4 border-l-yellow-400 font-semibold' :
                          isAdded ? 'bg-green-500/10 border-l-4 border-l-green-400 font-semibold' : 'hover:bg-white/5'
                          }`}
                      >
                        <td className="p-2.5 font-bold text-yellow-400">{item.ticker}</td>
                        <td className="p-2.5">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isUpdated ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40' :
                            isAdded ? 'bg-green-500/20 text-green-300 border border-green-500/40' :
                              'bg-gray-800 text-gray-400'
                            }`}>
                            {item.changeType}
                          </span>
                        </td>
                        <td className="p-2.5 text-gray-400">
                          {item.before ? `Qty: ${item.before.quantity} | Cost: $${item.before.averageCost}` : '-'}
                        </td>
                        <td className="p-2.5 text-white">
                          {item.after ? `Qty: ${item.after.quantity} | Cost: $${item.after.averageCost}` : 'ถูกลบออก'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-4 border-t border-gray-800 sticky bottom-0 bg-[#18181b] pb-2">
              <button onClick={() => setPreviewData(null)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold px-4 py-2 rounded text-sm transition-colors">
                ← เลือกไฟล์ใหม่
              </button>
              <button onClick={handleCommitCsv} disabled={loading} className="bg-green-500 hover:bg-green-600 text-black font-bold px-5 py-2 rounded text-sm transition-colors disabled:opacity-50">
                {loading ? 'กำลังบันทึก...' : '🚀 ยืนยันบันทึกข้อมูล (Commit)'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 📈 Modal สำหรับใส่หุ้นแบบแทนที่ (Replace Snapshot) พร้อม SearchBar และ Preview
// 📈 Modal สำหรับใส่หุ้นแบบแทนที่ (Replace Snapshot) พร้อมล็อก Ticker เดิม, ไฮไลท์การแก้ไข และ SearchBar
const AddAdminStockModal = ({ isOpen, onClose, portfolioId, currentHoldings, onRefresh }: any) => {
  const [items, setItems] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<any | null>(null);

  // เก็บต้นฉบับเพื่อเอาไว้เช็คว่ามีการเปลี่ยนแปลง (Modified) หรือไม่
  const [originalMap, setOriginalMap] = useState<{ [ticker: string]: { qty: string; averageCost: string } }>({});

  useEffect(() => {
    if (isOpen) {
      const map: { [ticker: string]: { qty: string; averageCost: string } } = {};
      if (currentHoldings && currentHoldings.length > 0) {
        const initialItems = currentHoldings.map((h: any) => {
          const ticker = h.stockSymbol || h.symbol || '';
          const qty = String(h.quantity || h.qty || '');
          const averageCost = String(h.avgBuyPrice || h.averageCost || '');
          map[ticker.toUpperCase()] = { qty, averageCost };
          return {
            ticker,
            qty,
            averageCost,
            isOriginal: true // ระบุว่าเป็นหุ้นเดิมในพอร์ต
          };
        });
        setItems(initialItems);
      } else {
        setItems([{ ticker: '', qty: '', averageCost: '', isOriginal: false }]);
      }
      setOriginalMap(map);
      setSearchQuery('');
      setPreviewResult(null);
    }
  }, [isOpen, currentHoldings]);


  const handleAddRow = () => setItems([...items, { ticker: '', qty: '', averageCost: '', isOriginal: false }]);

  const handleRemoveRow = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems.length > 0 ? newItems : [{ ticker: '', qty: '', averageCost: '', isOriginal: false }]);
  };

  const handleChangeRow = (index: number, field: string, value: string) => {
    const newItems = [...items];
    newItems[index][field as keyof typeof newItems[0]] = value;
    setItems(newItems);
  };

  const filteredItemsWithIndex = useMemo(() => {
    return items
      .map((item, originalIndex) => ({ ...item, originalIndex }))
      .filter(item => item.ticker.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [items, searchQuery]);

  const handlePreviewBulkAdd = async () => {
    for (const item of items) {
      if (!item.ticker || !item.qty || !item.averageCost) {
        toast.error('กรุณากรอกข้อมูลหุ้นให้ครบทุกช่อง');
        return;
      }
    }

    try {
      setLoading(true);
      const formattedItems = items.map(i => ({
        ticker: i.ticker.toUpperCase().trim(),
        qty: Number(i.qty),
        averageCost: Number(i.averageCost)
      }));

      const previewRes = await previewResetSnapshot(portfolioId, {
        mode: "RESET_SNAPSHOT",
        cashBalance: 0,
        asOfDate: new Date().toISOString(),
        timezone: "Asia/Bangkok",
        remark: `Replace snapshot stocks preview`,
        idempotencyKey: `admin-replace-preview-${Date.now()}`,
        items: formattedItems
      });

      setPreviewResult(previewRes?.data || previewRes);
      toast.success('สร้างพรีวิวรายการหุ้นสำเร็จ');
    } catch (error: any) {
      console.error(error);
      toast.error(error?.th_message || error?.message || 'ไม่สามารถพรีวิวหุ้นได้');
    } finally {
      setLoading(false);
    }
  };

  const handleCommitBulkAdd = async () => {
    const pToken = previewResult?.previewToken;
    if (!pToken) {
      toast.error('ไม่พบ Token ยืนยัน');
      return;
    }

    try {
      setLoading(true);
      await commitImportPreview(portfolioId, {
        previewToken: pToken,
        confirmationCode: previewResult?.confirmation?.code || "RESET_SNAPSHOT_CONFIRM"
      } as any);

      toast.success('บันทึกแทนที่ข้อมูลหุ้นในพอร์ตสำเร็จ!');
      onRefresh();
      onClose();
      setPreviewResult(null);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.th_message || error?.message || 'ไม่สามารถบันทึกหุ้นได้');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-[#18181b] border border-yellow-500/30 w-full max-w-2xl rounded-2xl p-6 text-white shadow-2xl max-h-[70vh] md:max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-3">
          <h2 className="text-lg font-bold text-yellow-400">🔄 ใส่หุ้นแบบแทนที่ (Replace Snapshot)</h2>
          <button onClick={() => { onClose(); setPreviewResult(null); }} className="text-gray-400 hover:text-white font-bold">✕</button>
        </div>

        {!previewResult ? (
          <>
            <p className="text-xs text-gray-400 mb-3">
              * หุ้นเดิมในพอร์ตจะไม่สามารถแก้ชื่อ Ticker ได้ แต่แก้จำนวนและต้นทุนได้ แถวที่ถูกแก้ไขหรือเพิ่มใหม่จะเปลี่ยนสีพื้นหลังให้เห็นเด่นชัด
            </p>

            <div className="mb-3">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="🔍 ค้นหา Ticker หุ้นในรายการ..."
                className="w-full bg-black/40 border border-gray-700 rounded-xl p-2.5 text-xs text-white focus:border-yellow-400 outline-none"
              />
            </div>

            <div className="space-y-3 overflow-y-auto flex-1 pr-1 my-1">
              {filteredItemsWithIndex.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-xs">ไม่พบหุ้นที่ค้นหา "{searchQuery}"</div>
              ) : (
                filteredItemsWithIndex.map(({ originalIndex, isOriginal, ...item }) => {
                  const upperTicker = item.ticker.toUpperCase().trim();
                  const orig = originalMap[upperTicker];
                  // เช็คว่ามีการแก้ไขค่า หรือเป็นหุ้นใหม่ (New Position) เพื่อเปลี่ยนสีพื้นหลัง
                  const isModified = !isOriginal || !orig || orig.qty !== item.qty || orig.averageCost !== item.averageCost;

                  return (
                    <div
                      key={originalIndex}
                      className={`p-3.5 rounded-xl border transition-colors ${isModified
                        ? 'bg-yellow-500/15 border-yellow-500/50 shadow-[0_0_10px_rgba(234,179,8,0.2)]'
                        : 'bg-black/30 border-gray-800'
                        }`}
                    >
                      {/* 🌟 มือถือใช้ Grid 2 บรรทัด / คอมพิวเตอร์ใช้ Flex แถวเดียว */}
                      <div className="grid grid-cols-2 sm:flex sm:flex-row gap-3 sm:items-end">
                        
                        {/* 1. Ticker */}
                        <div className="col-span-1 sm:flex-1">
                          <label className="text-[10px] text-gray-400">Ticker {isOriginal && '(ล็อค)'}</label>
                          <input
                            type="text"
                            value={item.ticker}
                            disabled={isOriginal}
                            onChange={e => handleChangeRow(originalIndex, 'ticker', e.target.value)}
                            className={`w-full rounded-lg p-2.5 text-xs mt-1 outline-none uppercase transition-colors ${isOriginal
                              ? 'bg-gray-800/60 border border-gray-700 text-gray-400 cursor-not-allowed'
                              : 'bg-black/50 border border-gray-600 text-white focus:border-yellow-400'
                              }`}
                            placeholder="AAPL"
                          />
                        </div>

                        {/* 2. จำนวน (Qty) */}
                        <div className="col-span-1 sm:w-24">
                          <label className="text-[10px] text-gray-400">จำนวน (Qty)</label>
                          <input
                            type="number"
                            value={item.qty}
                            onChange={e => handleChangeRow(originalIndex, 'qty', e.target.value)}
                            className="w-full bg-black/50 border border-gray-600 rounded-lg p-2.5 text-xs mt-1 focus:border-yellow-400 outline-none text-white transition-colors"
                            placeholder="10"
                          />
                        </div>

                        {/* 3. ต้นทุนเฉลี่ย & ปุ่มลบ (ในมือถือจะปัดลงมาอยู่บรรทัดที่ 2 เต็มๆ) */}
                        <div className="col-span-2 sm:w-[140px] flex gap-2 items-end">
                          <div className="flex-1">
                            <label className="text-[10px] text-gray-400">ต้นทุนเฉลี่ย ($)</label>
                            <input
                              type="number"
                              value={item.averageCost}
                              onChange={e => handleChangeRow(originalIndex, 'averageCost', e.target.value)}
                              className="w-full bg-black/50 border border-gray-600 rounded-lg p-2.5 text-xs mt-1 focus:border-yellow-400 outline-none text-white transition-colors"
                              placeholder="150"
                            />
                          </div>
                          
                          {/* ปุ่มถังขยะ: ทำเป็นปุ่มสี่เหลี่ยมสวยๆ เพื่อให้กดง่ายในมือถือ */}
                          <button 
                            type="button" 
                            onClick={() => handleRemoveRow(originalIndex)} 
                            className="shrink-0 w-9 h-9 mb-0.5 flex items-center justify-center bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500 hover:text-white rounded-lg transition-colors" 
                            title="ลบแถวนี้"
                          >
                            <span className="text-sm">🗑️</span>
                          </button>
                        </div>

                      </div>
                    </div>
                  );
                })
              )}

              <button type="button" onClick={handleAddRow} className="w-full py-2.5 bg-gray-800 hover:bg-gray-700 text-yellow-400 border border-dashed border-gray-600 rounded-xl text-xs font-bold transition-colors">
                + เพิ่มหุ้นตำแหน่งใหม่ (New Position)
              </button>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-800 mt-2">
              <button onClick={() => { onClose(); setPreviewResult(null); }} className="bg-gray-700 hover:bg-gray-600 text-white font-bold px-4 py-2 rounded text-sm transition-colors">
                ยกเลิก
              </button>
              <button onClick={handlePreviewBulkAdd} disabled={loading} className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-5 py-2 rounded text-sm transition-colors disabled:opacity-50">
                {loading ? 'กำลังพรีวิว...' : '🔍 ตรวจสอบพรีวิว (Preview)'}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-4 flex-1 overflow-y-auto pr-1">
            <div className="bg-black/40 p-3 rounded-xl border border-gray-800 flex justify-between text-xs">
              <span>รวมทั้งหมด: <b>{previewResult?.summary?.rowCount}</b></span>
              <span className="text-yellow-400">อัปเดต/เปลี่ยนแปลง: <b>{previewResult?.summary?.positionUpdatedCount || 0}</b></span>
              <span className="text-green-400">เพิ่มใหม่: <b>{previewResult?.summary?.positionAddedCount || 0}</b></span>
              <span className="text-red-400">ถูกถอดออก: <b>{previewResult?.summary?.positionRemovedCount || 0}</b></span>
            </div>

            <div className="border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-900 text-gray-400 border-b border-gray-800">
                    <th className="p-2.5">Ticker</th>
                    <th className="p-2.5">สถานะ</th>
                    <th className="p-2.5">ก่อนเปลี่ยน (Before)</th>
                    <th className="p-2.5">หลังเปลี่ยน (After)</th>
                  </tr>
                </thead>
                <tbody>
                  {previewResult?.changes?.map((item: any, idx: number) => {
                    const isUpdated = item.changeType === 'UPDATED';
                    const isAdded = item.changeType === 'ADDED';
                    const isRemoved = item.changeType === 'REMOVED';
                    return (
                      <tr
                        key={idx}
                        className={`border-b border-gray-800/60 ${isUpdated ? 'bg-yellow-500/10 border-l-4 border-l-yellow-400 font-semibold' :
                          isAdded ? 'bg-green-500/10 border-l-4 border-l-green-400 font-semibold' :
                            isRemoved ? 'bg-red-500/10 border-l-4 border-l-red-400 font-semibold' : 'hover:bg-white/5'
                          }`}
                      >
                        <td className="p-2.5 font-bold text-yellow-400">{item.ticker}</td>
                        <td className="p-2.5">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isUpdated ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40' :
                            isAdded ? 'bg-green-500/20 text-green-300 border border-green-500/40' :
                              isRemoved ? 'bg-red-500/20 text-red-300 border border-red-500/40' :
                                'bg-gray-800 text-gray-400'
                            }`}>
                            {item.changeType}
                          </span>
                        </td>
                        <td className="p-2.5 text-gray-400">
                          {item.before ? `Qty: ${item.before.quantity} | Cost: $${item.before.averageCost}` : '-'}
                        </td>
                        <td className="p-2.5 text-white">
                          {item.after ? `Qty: ${item.after.quantity} | Cost: $${item.after.averageCost}` : 'ถูกลบออก'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-4 border-t border-gray-800 sticky bottom-0 bg-[#18181b] pb-2">
              <button onClick={() => setPreviewResult(null)} className="bg-gray-700 hover:bg-gray-600 text-white font-bold px-4 py-2 rounded text-sm transition-colors">
                ← กลับไปแก้ไข
              </button>
              <button onClick={handleCommitBulkAdd} disabled={loading} className="bg-green-500 hover:bg-green-600 text-black font-bold px-5 py-2 rounded text-sm transition-colors disabled:opacity-50">
                {loading ? 'กำลังบันทึก...' : '🚀 ยืนยันบันทึกแทนที่ (Commit)'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminShayPortfolioDetailPage = () => {
  const { id, portId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currency, setCurrency] = useState<'USD' | 'THB'>('USD');
  const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { shayPortfolios, adminPortfolios } = usePortfolio();
  const { currentPrices, fetchPricesForSymbols, isLoadingPrices } = useMarketPrices();
  const { exchangeRates } = useExchangeRates();
  const { userInfo } = useUser();
  const isAdmin = Boolean(userInfo?.isAdmin);
  const passedPortfolioData = location.state?.portfolioData;
  const [portfolioDetail, setPortfolioDetail] = useState<any>(passedPortfolioData || null);
  const [loading, setLoading] = useState(!passedPortfolioData);

 const fetchPort = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      let formattedHoldings = [];
      let portData = null;

      // ✅ เช็คว่ามีข้อมูลส่งผ่านมาทาง state (จากหน้าแอดมิน) หรือไม่
      const passedData = location.state?.portfolioData;

      if (passedData) {
        // ถ้ามีข้อมูลส่งมาแล้ว ใช้ข้อมูลนั้นเป็นตั้งต้นได้เลย
        portData = passedData;
        
        // แกะโครงสร้าง subPorts / holdings ออกมาเตรียมแสดงผล
        const rawHoldings = portData?.subPorts?.[0]?.holdings || portData?.subPorts?.[0]?.stocks || [];
        formattedHoldings = rawHoldings.map((h: any) => ({
          ...h,
          stockSymbol: h.symbol || h.ticker || h.assetSymbol,
          avgBuyPrice: h.avgEntryPrice || h.averageCost || h.avgBuyPrice || 0,
          quantity: h.quantity || 0,
        }));
      } else {
        // 🔄 ถ้าไม่มีการส่ง state มา (เช่น ผู้ใช้กด Refresh หน้าเว็บตรงๆ) ให้วิ่งไปยิง API ดึงข้อมูลตามเดิม
        const allAvailablePorts = [...(shayPortfolios || []), ...(adminPortfolios || [])];
        const matchedPort = allAvailablePorts.find((p: any) => p.portfolioId === id || p.id === id || p.adminPortfolioId === id || p.slug === id);
        const targetSlug = matchedPort?.slug || id;

        if (isAdmin) {
          const res = await getAdminPortfolioById(id);
          portData = res?.data?.portfolio || res?.portfolio || res?.data || res;

          const latestSnapshotId = portData?.latestSnapshotId;
          if (latestSnapshotId) {
            const snapRes = await getSnapshotDetail(id, latestSnapshotId);
            const snapData = snapRes?.data || snapRes;
            const positions = snapData.snapshot?.positions || snapData.positions || snapData.items || [];
            formattedHoldings = positions.map((i: any) => ({
              ...i,
              stockSymbol: i.ticker || i.stockSymbol || i.symbol,
              avgBuyPrice: i.averageCost || i.avgBuyPrice || 0,
              quantity: i.quantity || i.qty || 0,
            }));
          }
        } else {
          portData = matchedPort;
          if (!portData) {
            const res = await getPublicPortfolioBySlug(targetSlug);
            portData = res?.data?.portfolio || res?.data || res;
          }

          const rawHoldings = portData?.subPorts?.[0]?.holdings || portData?.subPorts?.[0]?.stocks || [];
          formattedHoldings = rawHoldings.map((h: any) => ({
            ...h,
            stockSymbol: h.symbol || h.ticker || h.assetSymbol,
            avgBuyPrice: h.avgEntryPrice || h.averageCost || h.avgBuyPrice || 0,
            quantity: h.quantity || 0,
          }));
        }
      }

      const normalizedPortfolio = {
        ...portData,
        name: portData?.name || 'Admin Portfolio',
        subPorts: [
          {
            subPortId: 'main-subport',
            name: portData?.name || 'Main Portfolio',
            stocks: formattedHoldings,
            holdings: formattedHoldings,
            summary: portData?.summary || { cashValue: 0 }
          }
        ]
      };

      setPortfolio(normalizedPortfolio);

      const symbols = formattedHoldings.map((i: any) => i.stockSymbol).filter(Boolean);
      if (symbols.length > 0) {
        fetchPricesForSymbols(symbols);
      }
    } catch (error) {
      console.error(error);
      toast.error('ไม่สามารถโหลดข้อมูลพอร์ตได้');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPort();
  }, [id, refreshTrigger, shayPortfolios, adminPortfolios]);

  const convertCurrency = (val: number) => (currency === 'THB' ? val * (exchangeRates['THB'] || 1) : val);

  const currentSubPort = portfolio?.subPorts?.[0] || { stocks: [], holdings: [] };

  if (isLoading) {
    return <div className="text-center mt-20 text-gray-400">กำลังโหลดข้อมูลพอร์ต...</div>;
  }
  return (
    <div className=" p-6 md:p-2 text-white max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b border-gray-800 pb-4">
        <div>

          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold text-white">{portfolio?.name}</h1>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${isAdmin ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40' : 'bg-green-500/20 text-green-400 border border-green-500/40'}`}>
              {isAdmin ? 'พอร์ตแอดมิน' : 'พอร์ตสาธารณะ'}
            </span>
          </div>
          <p className="text-sm text-gray-400 mt-1">{portfolio?.description}</p>
        </div>

        <div className="flex items-center gap-3">
          {/* 🔒 แสดงปุ่มจัดการเฉพาะ Admin เท่านั้น */}
          {isAdmin && !location.state?.fromAdmin&& (
            <>
              <button
                onClick={() => setIsAddStockModalOpen(true)}
                className="bg-yellow-400 text-black hover:bg-yellow-500 font-bold px-4 py-2 rounded-lg text-sm transition-all shadow-md"
              >
                จัดการหุ้น
              </button>

              <button
                onClick={() => setIsCsvModalOpen(true)}
                className="bg-gray-700 text-yellow-400 border border-yellow-500/40 hover:bg-gray-600 font-bold px-4 py-2 rounded-lg text-sm transition-all shadow-md"
              >
                📂 ใช้ CSV
              </button>
            </>
          )}

          <div className="bg-[var(--component-bg-color)] p-1 rounded-lg border border-gray-700">
            <ToggleCurrency selectedCurrency={currency} onToggle={() => setCurrency(c => c === 'USD' ? 'THB' : 'USD')} />
          </div>
        </div>
      </div>

     <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 items-start">
        <div className="lg:col-span-2 col-span-1">
          <div className="h-full rounded-xl p-2 bg-[var(--component-bg-color)] border border-gray-800 shadow-lg">
            <PortfolioSingleChartBox
              isAdminPort={isAdmin}
              portfolios={currentSubPort}
              currentPrices={currentPrices}
              currency={currency}
              convertCurrency={convertCurrency}
              setIsConfigPopupOpen={() => { }}
              isShay={true}
            />
          </div>
        </div>

        <div className="lg:col-span-4 col-span-1 flex flex-col">
          <div className="bg-[var(--component-bg-color)] rounded-xl p-4 border border-gray-800 shadow-lg ">
            <div className="flex justify-between items-center mb-4 border-b border-gray-800 pb-3">
              <h2 className="text-lg font-bold text-yellow-400 flex items-center gap-2" style={{ color: "var(--yellow-text-color)" }}>
                <span>📊</span> หุ้นในพอร์ต
              </h2>
            </div>

              <PortfolioStocksBox
                portfolio={currentSubPort}
                onBack={() => {
                  // ✅ เช็คว่าถ้ามาจากหน้าแอดมิน ให้กลับไปหน้าแอดมิน
                  if (location.state?.fromAdmin) {
                    navigate('/admin');
                  } else {
                    navigate('/portfolio');
                  }
                }}
                portfolioId={id}
                isPortAdmin={isAdmin}
                currency={currency}
                convertCurrency={convertCurrency}
                currentPrices={currentPrices}
                onDeleteStock={() => { }}
                onEditStock={() => { }}
                onMoveStock={() => { }}
                onSelectStock={() => { }}
                onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                setIsSellBuyModalOpen={() => { }}
                setIsImportModalOpen={() => { }}
                onNavigateToPendingDividend={() => { }}
                fromShayAdmin={true}
              />
         
          </div>
        </div>
      </div>

      {isAdmin && (
        <>
          <AddAdminStockModal
            isOpen={isAddStockModalOpen}
            onClose={() => setIsAddStockModalOpen(false)}
            portfolioId={id}
            currentHoldings={currentSubPort.stocks || []}
            onRefresh={() => setRefreshTrigger(prev => prev + 1)}
          />

          <ImportCsvModal
            isOpen={isCsvModalOpen}
            onClose={() => setIsCsvModalOpen(false)}
            portfolioId={id}
            onRefresh={() => setRefreshTrigger(prev => prev + 1)}
          />
        </>
      )}
    </div>
  );
};

export default AdminShayPortfolioDetailPage;